// /auth/adopt — the agent's auto-login handoff.
//
// An agent that just created the account already holds a refresh token (the
// agent code), and a refresh token is all Cognito needs to mint a live session.
// So it can hand the human a single URL that lands them signed in, with no
// password typed and nothing to copy. That is the whole trick; there is no new
// infrastructure behind it.
//
// WHY THE CODE RIDES IN THE #FRAGMENT, NOT THE QUERY STRING:
// a fragment is never sent to the server. It does not reach CloudFront, it does
// not appear in S3 access logs, and it is not in the Referer header of any
// subsequent request. A `?code=` would have been written to a log we don't
// control, forever. This is the same reason OAuth implicit flow used fragments.
//
// WHAT WE STILL CAN'T PREVENT, STATED PLAINLY: the URL is a credential for as
// long as the code lives. It lands in the user's browser history. We strip it
// from the address bar with replaceState the moment it's consumed, which keeps
// it out of the *page* URL and out of anything the app later links to — but
// history is history. The mitigating fact is that this code is one the agent
// already has, in its .env and its scrollback; the marginal exposure is the
// browser's own history, not a new secret. A truly one-time link needs a
// Cognito CUSTOM_AUTH challenge, which is a bigger build (see docs/ONBOARDING.md).
//
// If that tradeoff is ever unacceptable for a deployment, the mitigation is to
// revoke: /app → Connect an agent → Revoke agent access.

import { Alert, Anchor, Button, Card, Group, Loader, Stack, Text, Title } from "@mantine/core";
import { IconRobot } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adoptAgentCode } from "../lib/cognito.ts";

/** Pull the code out of the fragment and ERASE it from the address bar in the
 *  same breath, so a screenshot or a shoulder-surf doesn't catch it. */
function takeCodeFromFragment(): string | null {
	const hash = window.location.hash.replace(/^#/, "");
	if (!hash) return null;
	const params = new URLSearchParams(hash);
	const code = params.get("code");
	if (!code) return null;
	window.history.replaceState(null, "", `${window.location.pathname}`);
	return code;
}

export function AuthAdopt() {
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const code = takeCodeFromFragment();
		if (!code) {
			setError(
				"This link is missing its login code. Ask your agent for a fresh one (`s3p login-link`), or sign in with your email and password.",
			);
			return;
		}
		void (async () => {
			try {
				await adoptAgentCode(code);
				// Full navigation, so every provider re-hydrates from the session we
				// just wrote — same as the password sign-in path. `/` is the app list
				// on this host; this page only ever runs here now (the apex forwards
				// it), so the session we just adopted is the one the list reads.
				window.location.assign("/");
			} catch (e) {
				const msg = e instanceof Error ? e.message : String(e);
				setError(
					/invalid_grant|NotAuthorized|revoked/i.test(msg)
						? "That login link has expired or been revoked. Ask your agent for a fresh one (`s3p login-link`), or sign in with your email and password."
						: msg,
				);
			}
		})();
	}, []);

	if (error) {
		return (
			<Stack gap="lg" maw={460} mx="auto">
				<Alert color="red" variant="light" title="Couldn't sign you in">
					{error}
				</Alert>
				<Button component={Link} to="/signin">
					Sign in with email
				</Button>
			</Stack>
		);
	}

	return (
		<Stack gap="lg" maw={460} mx="auto">
			<Card withBorder radius="md" padding="xl">
				<Stack align="center" gap="md">
					<IconRobot size={32} />
					<Title order={3}>Signing you in…</Title>
					<Text c="dimmed" size="sm" ta="center">
						Your agent set this up. No password needed.
					</Text>
					<Group justify="center">
						<Loader size="sm" />
					</Group>
				</Stack>
			</Card>
			<Text size="xs" c="dimmed" ta="center">
				Taking too long?{" "}
				<Anchor component={Link} to="/signin">
					Sign in with your email
				</Anchor>
				.
			</Text>
		</Stack>
	);
}
